/**
 * @file Collection management for RxDB collections
 * This composable handles reactive collection creation with two-phase initialization.
 * @since 2025-10-15
 */

const { ref, shallowRef, computed, watch, readonly, markRaw } = Vue;
import { useErrorHandler } from '../../core/errors.js';
import { DatabaseError } from '../../utils/errors.js';
import { useDb } from '../../core/db.js';
import { useDocument } from './document.js';

const db = useDb();

// Global registry of all collections
const _registry = new Map();

/**
 * Get all registered collections
 * @returns {Array} Array of collection objects
 */
export function _getRegisteredCollections() {
  return Array.from(_registry.values());
}

/**
 * Get a collection by name
 * @param {string} collectionName - Name of the collection
 * @returns {object} Collection object
 * @throws {Error} If collection not found
 */
export function getCollection(collectionName) {
  const collection = _registry.get(collectionName);
  if (!collection) {
    throw new Error(`Collection '${collectionName}' not found`);
  }
  return collection;
}

/**
 * Create and return a reactive collection object
 *
 * @param {object} collectionDef - Collection definition
 * @param {string} collectionDef.name - Unique collection identifier
 * @param {object} collectionDef.schema - RxDB schema definition
 * @param {object} [collectionDef.document] - Document-level definitions (methods, computed, watch)
 * @param {object} [collectionDef.document.methods] - Custom document instance methods
 * @param {object} [collectionDef.document.computed] - Document computed properties
 * @param {object} [collectionDef.document.watch] - Document watchers
 * @param {object} [collectionDef.collection] - Collection-level definitions (methods, computed, watch)
 * @param {object} [collectionDef.collection.methods] - Custom collection instance methods
 * @param {object} [collectionDef.collection.computed] - Collection computed properties
 * @param {object} [collectionDef.collection.watch] - Collection watchers
 * @param {object} [collectionDef.migrationStrategies] - Schema version migrations
 * @param {Function} [collectionDef.setupReplication] - Optional replication setup function
 * @returns {object} Reactive collection object
 */
export function useCollection(collectionDef) {
  if (!collectionDef || !collectionDef.name) {
    throw new Error('Collection definition must have a name');
  }
  if (!collectionDef.schema) {
    throw new Error(`Collection '${collectionDef.name}' must have a schema`);
  }

  const collectionName = collectionDef.name;

  // Return existing collection if already registered
  if (_registry.has(collectionName)) {
    return _registry.get(collectionName);
  }

  // Populated once db is initialized
  let rxCollection = null;
  let replicationState = null;
  let ready = false;

  // Create promise that resolves when init process completes
  let initPromiseResolve = null;
  let initPromiseReject = null;
  const initPromise = new Promise((resolve, reject) => {
    initPromiseResolve = resolve;
    initPromiseReject = reject;
  });

  // Event emitter for sync events
  const events = shallowRef(new EventTarget());

  // Sync status tracking
  const isSyncing = Vue.ref(false);
  const syncErrors = Vue.ref([]);
  const isReady = Vue.ref(false);
  const isInSync = Vue.ref(false);
  const isLocalOnly = Vue.ref(false);
  const isMigrating = Vue.ref(false);
  const migrationProgress = Vue.ref(null);
  const lastSuccessfulSync = Vue.ref(null);
  const lastSyncErrorMessage = Vue.ref('Data is not synchronized');
  // Plain Map: lookups need identity, not reactivity (the `items` ref handles
  // template updates). Avoids per-key reactivity bookkeeping for large sets.
  const documents = new Map();
  const items = Vue.ref([]);
  const pkPath = collectionDef.schema.primaryKey;

  let subscription = null;
  let collection = null;

  const cleanupInputData = data => {
    if (data === null || data === undefined) {
      return data;
    }
    return JSON.parse(JSON.stringify(data));
  };

  function processUpdate(results) {
    try {
      const pks = new Set();
      const newItems = [];
      results.forEach(doc => {
        const pk = doc[pkPath];
        if (!pk) {
          console.warn('Document has empty primary key', doc);
          return;
        }
        pks.add(pk);
        let currDoc = documents.get(pk);
        if (!currDoc) {
          currDoc = useDocument(collection, pk);
          documents.set(pk, currDoc);
        }
        newItems.push(currDoc);
        if (currDoc.$raw === doc) return;
        currDoc.$$setRaw(markRaw(doc));
      });
      const oldItems = items.value;
      const itemsChanged =
        oldItems.length !== newItems.length ||
        newItems.some((doc, i) => doc !== oldItems[i]);
      if (itemsChanged) {
        items.value = newItems;
      }
      for (const pk of documents.keys()) {
        if (!pks.has(pk)) documents.delete(pk);
      }
      if (!isReady.value) isReady.value = true;
    } catch (conversionError) {
      console.error('[useCollection] Data conversion error:', conversionError);
      console.trace(conversionError);
    }
  }

  // Create reactive collection object
  collection = Vue.reactive({
    // Immediate access
    name: collectionDef.name,
    schema: collectionDef.schema,
    documentDef: collectionDef.document || null,
    conflictHandler: collectionDef.conflictHandler || null,

    // Reactive state from collectionView
    items,
    loading: false,
    error: false,

    // CRUD operations from collectionView
    get: id => documents.get(id),
    async insert(doc) {
      await initPromise;
      doc = cleanupInputData(doc);
      return await this.rawCollection.insert(doc);
    },
    async update(id, dataToUpdate) {
      await initPromise;
      dataToUpdate = cleanupInputData(dataToUpdate);
      const doc = documents.get(id)?.$raw;
      if (!doc) throw Error(`Document ${id} does not exist.`);

      const { setData, unsetData } = Object.entries(dataToUpdate).reduce(
        (acc, [key, value]) => {
          if (value === null || value === undefined) {
            acc.unsetData[key] = '';
          } else {
            acc.setData[key] = value;
          }
          return acc;
        },
        { setData: {}, unsetData: {} }
      );

      await doc.modify(docToModify => {
        Object.keys(unsetData).forEach(key => delete docToModify[key]);
        Object.entries(setData)
          .filter(([key]) => key in collectionDef.schema.properties)
          .forEach(([key, value]) => (docToModify[key] = value));
        return docToModify;
      });

      return doc;
    },
    async remove(id) {
      await initPromise;
      const doc = documents.get(id)?.$raw;
      if (doc) {
        await doc.remove();
      } else {
        throw Error(`Document ${id} does not exist.`);
      }
    },
    async upsert(doc) {
      await initPromise;
      return await this.rawCollection.upsert(cleanupInputData(doc));
    },
    rawCollection: null,
    replicationState: null,
    documents,

    // Sync status
    isSyncing,
    syncErrors,
    isReady,
    isInSync,
    isLocalOnly,
    isMigrating,
    migrationProgress,
    lastSuccessfulSync,
    lastSyncErrorMessage,
    events,

    /**
     * Get the underlying RxDB collection instance
     * @returns {Promise<object>}
     */
    async getInstance() {
      await initPromise;
      return rxCollection;
    },

    /**
     * Get replication state if configured
     * @returns {Promise<object|null>}
     */
    async getReplicationState() {
      await initPromise;
      return replicationState;
    },

    /**
     * Manually trigger full resync
     * @returns {Promise<void>}
     */
    async resync() {
      await initPromise;
      if (!replicationState) {
        throw new Error(
          `Collection '${collectionName}' has no replication configured`
        );
      }
      return replicationState.reSync();
    },

    /**
     * Hard reset: delete all local data and reload page
     * @returns {Promise<void>}
     */
    async hardReset() {
      if (
        !confirm(
          `Are you sure you want to hard reset collection '${collectionName}'? This will delete all local data and reload the page.`
        )
      ) {
        return;
      }
      await initPromise;
      await rxCollection.remove();
      location.reload();
    },

    async checkDocument(docId) {
      // debug utility to ensure document is in sync
      await initPromise;
      if (!replicationState?.internalReplicationState?.input) {
        return;
      }
      const forkDocs =
        await replicationState.internalReplicationState.input.forkInstance.findDocumentsById(
          [docId],
          true
        );
      const forkDoc = forkDocs[0];
      // Get assumed master state from meta instance
      const metaId = docId + '|0'; // itemId + '|' + isCheckpoint
      const metaDocs =
        await replicationState.internalReplicationState.input.metaInstance.findDocumentsById(
          [metaId],
          true
        );
      const metaDoc = metaDocs[0];

      if (!metaDoc) {
        return { docId, reason: 'no meta document', forkDoc };
      }

      const assumedMaster = metaDoc.docData;

      // Compare (strip _meta/_rev for fair comparison)
      const forkStripped = { ...forkDoc };
      delete forkStripped._meta;
      delete forkStripped._rev;
      delete forkStripped._attachments;

      const masterStripped = { ...assumedMaster };
      delete masterStripped._meta;
      delete masterStripped._rev;
      delete masterStripped._attachments;

      const isEqual = this.rawCollection.conflictHandler.isEqual(
        forkStripped,
        masterStripped
      );

      if (!isEqual) {
        return {
          docId,
          reason: 'content differs',
          forkDoc: forkStripped,
          assumedMaster: masterStripped,
          metaDoc,
        };
      }
    },

    async checkSynchro() {
      await initPromise;
      const allDocs = await this.rawCollection.find().exec();
      console.log(`Starting check: ${allDocs.length} documents`);
      let normal = 0;
      let differs = 0;
      for (const doc of allDocs) {
        normal += 1;
        const result = await this.checkDocument(doc.primary);
        if (result) {
          differs += 1;
          console.log(result);
        }
      }
      console.log(
        `[collection:${this.name}] Check done: ${normal - differs}/${normal} in sync.`
      );
      if (differs) {
        console.warn(
          `[collection:${this.name}] ${differs} documents out of sync.`
        );
        return `${differs} documents out of sync`;
      }
      return null;
    },

    /**
     * Bind to RxDB database
     * @private
     */
    async _init() {
      if (ready) return;

      try {
        // Wait for database resolution
        await db.ensureInitialized();
        const rxdb = db.rawDb;

        // Register in database
        db.collections.push(collection);

        // Set up event forwarding to db
        events.value.addEventListener('document-received', event => {
          db.events.dispatchEvent(
            new CustomEvent('document-received', { detail: event.detail })
          );
        });
        events.value.addEventListener('document-sent', event => {
          db.events.dispatchEvent(
            new CustomEvent('document-sent', { detail: event.detail })
          );
        });

        // Create RxDB collection (migration handled manually below so we can surface its state)
        const cols = await rxdb.addCollections({
          [collectionName]: {
            schema: collectionDef.schema,
            migrationStrategies: collectionDef.migrationStrategies || {},
            conflictHandler: collectionDef.conflictHandler,
            localDocuments: true,
            autoMigrate: false,
          },
        });

        rxCollection = cols[collectionName];

        this.rawCollection = markRaw(rxCollection);

        // Run migration explicitly so isMigrating / progress are observable
        // and isReady (set later in processUpdate) stays false until migration completes.
        if (await rxCollection.migrationNeeded()) {
          isMigrating.value = true;
          const migrationState = rxCollection.getMigrationState();
          const migSub = migrationState.$.subscribe(status => {
            migrationProgress.value = status.count;
          });
          try {
            await rxCollection.migratePromise();
          } finally {
            migSub.unsubscribe();
            isMigrating.value = false;
            migrationProgress.value = null;
          }
        }

        // Load persisted sync metadata
        const syncMeta = await rxCollection.getLocal('sync-metadata');
        if (syncMeta) {
          lastSuccessfulSync.value = syncMeta.get('lastSuccessfulSync');
        }

        subscription = rxCollection.find().$.subscribe({
          next: processUpdate,
          error: err => {
            console.error('[useCollection] Observable error:', err);
          },
        });

        // Setup replication if configured
        if (collectionDef.setupReplication) {
          try {
            replicationState =
              await collectionDef.setupReplication(rxCollection);
            this.replicationState = markRaw(replicationState);

            let syncComplete = true;

            this.checkSynchro().then(error => {
              syncComplete = !error;
              if (error) {
                lastSyncErrorMessage.value = `Synchronisation incomplete (${error}), merci de signaler l'erreur à l'EMATT (tel 27621).`;
                syncErrors.value.push({
                  timestamp: Date.now(),
                  error: 'Synchronisation incomplete',
                });
              }
            });

            // Subscribe to replication observables
            replicationState.error$.subscribe(err => {
              isInSync.value = false;
              if (err.parameters?.errors?.[0]?.name === 'TypeError') {
                // Ignore network errors
                lastSyncErrorMessage.value =
                  'No response, you are probably offline.';
                return;
              }
              lastSyncErrorMessage.value =
                "Erreur de synchronisation, merci de signaler l'erreur à l'EMATT (tel 27621).";
              console.error(`Replication error in ${collectionName}:`, err);
              syncErrors.value.push({
                timestamp: Date.now(),
                error: err,
              });
            });

            let metaWriteQueue = Promise.resolve();
            let initialReplicationDone = false;

            const markSyncedNow = () => {
              isInSync.value = syncComplete;
              lastSyncErrorMessage.value = '';
              const now = new Date();
              lastSuccessfulSync.value = now.toISOString();
              metaWriteQueue = metaWriteQueue
                .then(() =>
                  rxCollection.upsertLocal('sync-metadata', {
                    lastSuccessfulSync: now.toISOString(),
                  })
                )
                .catch(e => console.error('unable to upsert sync-metadata', e));
            };

            // Don't claim "synchronized" until the first replication cycle has actually completed.
            // Otherwise active$ can briefly emit false on startup and falsely mark the collection in sync.
            replicationState.awaitInitialReplication().then(() => {
              initialReplicationDone = true;
              if (!isSyncing.value) markSyncedNow();
            });

            replicationState.active$.subscribe(active => {
              isSyncing.value = active;
              if (active) return;
              if (!initialReplicationDone) return;
              markSyncedNow();
            });

            replicationState.received$.subscribe(doc => {
              const event = new CustomEvent('document-received', {
                detail: { collectionName, timestamp: Date.now(), doc },
              });
              events.value.dispatchEvent(event);
            });

            replicationState.sent$.subscribe(doc => {
              const event = new CustomEvent('document-sent', {
                detail: { collectionName, timestamp: Date.now(), doc },
              });
              events.value.dispatchEvent(event);
            });
          } catch (err) {
            console.warn(
              `Replication setup failed for collection '${collectionName}':`,
              err
            );
          }
        } else {
          isLocalOnly.value = true;
        }

        ready = true;
        initPromiseResolve();

        console.log(`Collection '${collectionName}' initialized successfully`);
      } catch (err) {
        const { handleError } = useErrorHandler();
        const error = new DatabaseError(
          `Failed to bind collection '${collectionName}' to RxDB: ${err.message}`,
          'collections',
          err
        );
        await handleError(error, {
          operation: 'bindCollection',
          collectionName,
        });
        initPromiseReject(err);
        throw err;
      }
    },
  });

  // Add collection methods, computed properties, and watchers from definition
  const collectionConfig = collectionDef.collection || {};

  // Add custom methods
  if (collectionConfig.methods) {
    Object.entries(collectionConfig.methods).forEach(([name, fn]) => {
      if (name.startsWith('$')) {
        throw new Error(
          `Collection method '${name}' cannot start with '$' (reserved for internal properties)`
        );
      }
      collection[name] = fn.bind(collection);
    });
  }

  // Add computed properties
  if (collectionConfig.computed) {
    Object.entries(collectionConfig.computed).forEach(([name, getter]) => {
      if (name.startsWith('$')) {
        throw new Error(
          `Collection computed property '${name}' cannot start with '$' (reserved for internal properties)`
        );
      }
      collection[name] = computed(() => getter.call(collection));
    });
  }

  // Setup watchers
  if (collectionConfig.watch) {
    Object.entries(collectionConfig.watch).forEach(([key, watchDef]) => {
      const handler =
        typeof watchDef === 'function' ? watchDef : watchDef.handler;
      const options = typeof watchDef === 'object' ? watchDef : { deep: false };

      watch(
        () => collection[key],
        (newVal, oldVal) => handler.call(collection, newVal, oldVal),
        options
      );
    });
  }

  // Register collection in global registry
  _registry.set(collectionName, collection);

  // Auto-start binding
  collection._init();

  return collection;
}

/**
 * Default export for convenience
 * @default
 */
export default useCollection;
